import React, { forwardRef } from 'react';
import {
  MenuProps,
  PaperProps,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  SxProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  DateSelectorNormalizedStyle,
  PLACEHOLDER_COLOR,
} from '@/components/@IntranetLibrary/DatePicker/styles';
import {
  PlainSelectSx,
  SelectMenuItem,
  SelectMenuPaperSx,
} from '@/components/@IntranetLibrary/Select/styles';

export type SelectOption<T = string> = {
  label: string;
  /** 메뉴 항목을 글자 대신 직접 그릴 때. 고른 값 표시는 renderValue 로 따로 */
  labelNode?: React.ReactNode;
  value: T;
  disabled?: boolean;
  isHidden?: boolean;
};

/**
 * `outlined` — 기본. 흰 배경 + 보더 박스 (h 48)
 * `plain`    — 보더·배경 없이 값 + 화살표만. 툴바처럼 인라인으로 놓는 자리에 쓴다.
 *              박스만 벗기고 글자 크기·색은 호출부가 `sx` 로 정한다.
 */
export type SelectVariant = 'outlined' | 'plain';

export type SelectProps<T = string> = Omit<
  MuiSelectProps,
  'variant' | 'label' | 'MenuProps'
> & {
  sx?: SxProps<Theme>;
  variant?: SelectVariant;
  options: SelectOption<T>[];
  placeholder?: React.ReactNode;
  infoTextNode?: React.ReactNode;
  disablePortal?: boolean;
  PaperProps?: PaperProps;
  MenuProps?: Partial<Omit<MenuProps, 'disablePortal'>>;
};

// 선택된 값이 없으면 placeholder 를 유지한다.
// value 가 '' 인 경우만 보면 아직 값이 세팅되지 않은 undefined/null 에서
// placeholder 가 통째로 사라진다.
const isEmptyValue = (value: MuiSelectProps['value']) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const Select = forwardRef(
  <T extends string>(
    {
      sx,
      variant = 'outlined',
      options,
      placeholder,
      infoTextNode,
      renderValue,
      disablePortal,
      PaperProps,
      MenuProps,
      ...SelectProps
    }: SelectProps<T>,
    ref: React.Ref<HTMLDivElement>, // MuiSelect ref 전달
  ) => {
    return (
      <MuiSelect
        {...SelectProps}
        ref={ref}
        // 배열로 넘긴다 — 뒤가 앞을 덮으므로 **호출부 sx 가 마지막**이어야 한다.
        // (객체로 스프레드하면 sx 의 함수/배열 형태를 못 받는다)
        sx={[
          {
            ...DateSelectorNormalizedStyle,
            background: 'var(--dk-bg, #ffffff)',
            height: '48px',
            boxSizing: 'border-box',
            outline: 'none',
            '&:focus, &:focus-visible': { outline: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#000 !important',
              borderWidth: '1px !important',
            },
            '& .MuiSelect-select': {
              lineHeight: '20px',
              outline: 'none',
              '&:focus, &:focus-visible': { outline: 'none' },
            },
          },
          variant === 'plain' && PlainSelectSx,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        variant="outlined"
        displayEmpty
        MenuProps={{
          disablePortal,
          PaperProps: { sx: SelectMenuPaperSx, ...PaperProps },
          ...MenuProps,
        }}
        renderValue={
          renderValue ||
          (isEmptyValue(SelectProps?.value)
            ? () => (
                <span style={{ color: PLACEHOLDER_COLOR }}>{placeholder}</span>
              )
            : undefined)
        }
      >
        {infoTextNode}
        {options.map((option, index) => {
          const { label, labelNode, value, disabled, isHidden } = option;
          if (isHidden) return null;

          return (
            <SelectMenuItem
              key={`${label} - ${index}`}
              value={value}
              disabled={disabled}
            >
              {labelNode ?? label}
            </SelectMenuItem>
          );
        })}
      </MuiSelect>
    );
  },
);
Select.displayName = 'Select';

export default Select;
